/* ===== popup-refine.js =====
   Mobile-only floating refine modal
   - Accurate height shrink/grow (padding/border aware)
   - Respects safe-area bottom margin
   - Background inert while open
   - visualViewport-aware positioning & resizing
*/
(function () {
  const modal   = document.getElementById('motRefineModal');
  if (!modal) return;

  const panel   = modal.querySelector('.mot-modal__panel');
  const overlay = modal.querySelector('.mot-modal__overlay');
  const header  = modal.querySelector('.mot-modal__header');
  const bodyEl  = modal.querySelector('.mot-modal__body');
  const content = modal.querySelector('.mot-modal__content') || bodyEl;

  const MAX_W = 275, MIN_W = 160;
  const DESKTOP_MQ = '(min-width: 1241px)';
  const mqlDesktop = window.matchMedia ? window.matchMedia(DESKTOP_MQ) : null;

  let lastFocused = null, anchorEl = null, rafScheduled = false;
  let io = null, roPanel = null, roBody = null, roContent = null, mo = null;
  let imgListenerBound = false;
  let sizingWriteInProgress = false;
  let burstId = 0;
  let inertSet = null;
  let opening = false, closing = false, closeFinished = false;
  let prefersReducedMotion = false;
  try {
    prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (_) {}

  const focusableSelectors = [
    'a[href]','button:not([disabled])','input:not([disabled])',
    'select:not([disabled])','textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const clamp = (n, min, max) => Math.max(min, Math.min(n, max));
  const isDesktop = () => (mqlDesktop ? mqlDesktop.matches : window.innerWidth >= 1241);

  // ---------- Utils ----------
  function cssVarNumber(el, name, fallback = 0) {
    const v = getComputedStyle(el).getPropertyValue(name).trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  function getHeaderOffset() {
    var fromVar = cssVarNumber(document.documentElement, '--header-h',
           cssVarNumber(document.body, '--header-h', 0));
    if (fromVar) return fromVar;
    var siteHeader = document.getElementById('header');
    return siteHeader ? siteHeader.getBoundingClientRect().height : 0;
  }
  function getStickyTop() {
    var fromSticky = cssVarNumber(document.documentElement, '--collection-sticky-top', 0);
    if (fromSticky) return fromSticky;
    var headerH = getHeaderOffset();
    return headerH ? headerH + 20 : 0;
  }
  function getBottomMargin() {
    const root = document.documentElement;
    const v = getComputedStyle(root).getPropertyValue('--safe-bottom').trim();
    const safe = parseFloat(v) || 0;
    return Math.max(20, safe);
  }
  function isPositionFixed(el) {
    let node = el;
    while (node && node !== document.documentElement) {
      const cs = getComputedStyle(node);
      if (cs.position === 'fixed') return true;
      node = node.parentElement;
    }
    return false;
  }
  function isElementVisible(el) {
    if (!el || !el.isConnected) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return false;
    const vw = innerWidth, vh = innerHeight;
    return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
  }
  function safeFocus(el, opts) {
    if (!el) return;
    try {
      var focusOpts = { preventScroll: true };
      if (opts) {
        for (var k in opts) {
          if (Object.prototype.hasOwnProperty.call(opts, k)) focusOpts[k] = opts[k];
        }
      }
      el.focus(focusOpts);
    } catch (e1) {
      try { el.focus(); } catch (e2) {}
    }
  }
  function setStyleIfDiff(el, prop, value) {
    if (el.style[prop] !== value) el.style[prop] = value;
  }
  function setBackgroundInert(enable) {
    if (enable) {
      inertSet = new Set();
      Array.from(document.body.children).forEach(el => {
        if (el === modal) return;
        const tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK') return;
        if (!el.hasAttribute('inert')) {
          el.setAttribute('inert', '');
          inertSet.add(el);
        }
      });
    } else if (inertSet) {
      inertSet.forEach(el => el.removeAttribute('inert'));
      inertSet = null;
    }
  }
  function onTouchMoveLock(e) {
    if (bodyEl && bodyEl.contains(e.target)) return;
    e.preventDefault();
  }
  function setPageScrollLock(enable) {
    const html = document.documentElement;
    if (enable) {
      html.classList.add('mot-refine-open');
      document.addEventListener('touchmove', onTouchMoveLock, { passive: false });
    } else {
      html.classList.remove('mot-refine-open');
      document.removeEventListener('touchmove', onTouchMoveLock);
    }
  }

  async function scrollTriggerIntoFixedPosition(trigger, timeoutMs = 1200) {
    return new Promise((resolve) => {
      if (!trigger) return resolve(false);
      const targetTop = getStickyTop() || getHeaderOffset();
      const rect = trigger.getBoundingClientRect();
      const currentTop = rect.top;
      if (isPositionFixed(trigger) || Math.abs(currentTop - targetTop) <= 1) {
        return resolve(true);
      }
      const dest = Math.max(0, window.scrollY + currentTop - targetTop);
      let done = false;
      const finish = (ok) => { if (done) return; done = true; cleanup(); resolve(!!ok); };
      const onScroll = () => {
        const t = trigger.getBoundingClientRect().top;
        if (isPositionFixed(trigger) || Math.abs(t - targetTop) <= 1) finish(true);
      };
      const onScrollEnd = () => finish(true);
      let pollId = null;
      let mqHandler = null;
      function startPoll() {
        let tries = 0;
        pollId = window.setInterval(() => {
          tries++; onScroll();
          if (tries > 40) { clearInterval(pollId); pollId = null; finish(true); }
        }, 50);
      }
      function cleanup() {
        window.removeEventListener('scroll', onScroll);
        if ('onscrollend' in window) window.removeEventListener('scrollend', onScrollEnd);
        if (pollId) { clearInterval(pollId); pollId = null; }
        if (mqHandler && mqlDesktop) {
          if (mqlDesktop.removeEventListener) mqlDesktop.removeEventListener('change', mqHandler);
          else if (mqlDesktop.removeListener) mqlDesktop.removeListener(mqHandler);
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      if ('onscrollend' in window) window.addEventListener('scrollend', onScrollEnd, { passive: true });
      else startPoll();
      setTimeout(() => finish(true), timeoutMs);
      try {
        window.scrollTo({ top: dest, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      } catch (_) {
        window.scrollTo(0, dest);
      }
      mqHandler = function (e) { if (e.matches) finish(false); };
      if (mqlDesktop) {
        if (mqlDesktop.addEventListener) mqlDesktop.addEventListener('change', mqHandler);
        else if (mqlDesktop.addListener) mqlDesktop.addListener(mqHandler);
      }
    });
  }

  function ensureTriggerStillVisibleOrClose() {
    if (!anchorEl) return true;
    if (!isElementVisible(anchorEl)) { close('trigger_hidden'); return false; }
    return true;
  }

  // ---------- Scroll preservation ----------
  // Keep the list from jumping when a group expands. First layout has no
  // overflow (oldMax === 0), which used to look like "pinned to bottom" and
  // scrolled a long menu down as soon as height was capped.
  function preserveScrollDuring(writes) {
    if (!bodyEl) { writes(); return; }
    const sc = bodyEl;
    const oldTop = sc.scrollTop;
    const oldMax = Math.max(0, sc.scrollHeight - sc.clientHeight);
    const wasAtTop = oldTop <= 1 || oldMax <= 0;
    const distBottom = Math.max(0, oldMax - oldTop);

    sizingWriteInProgress = true;
    writes();
    sizingWriteInProgress = false;

    const restore = () => {
      if (wasAtTop) {
        sc.scrollTop = 0;
        return;
      }
      const newMax = Math.max(0, sc.scrollHeight - sc.clientHeight);
      let newTop = Math.max(0, newMax - distBottom);
      if (distBottom > (newMax - oldTop)) newTop = Math.min(oldTop, newMax);
      sc.scrollTop = newTop;
    };
    restore();
    requestAnimationFrame(restore);
  }

  // helper: run a few extra frames after content expands
  function burstRecalc(frames = 4) {
    const id = ++burstId;
    const tick = () => { if (id !== burstId) return; computeAndPlaceNow(); if (--frames > 0) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }

  // ---------- Positioning & sizing ----------
  function computeAndPlaceNow() {
    if (!anchorEl) return;
    if (!ensureTriggerStillVisibleOrClose()) return;
    if (isDesktop()) { close('desktop'); return; }
    if (sizingWriteInProgress) return;

    const vv  = visualViewport;
    const vw  = vv ? vv.width  : innerWidth;
    const vh  = vv ? vv.height : innerHeight;
    const BOTTOM_MARGIN = getBottomMargin();

    const rect = anchorEl.getBoundingClientRect();
    let top  = Math.max(0, rect.top);
    let left = Math.max(0, rect.left);

    preserveScrollDuring(() => {
      // ---- Width / left ----
      const rightEdge = vw - BOTTOM_MARGIN;
      const narrow = vw < MAX_W;
      const width = narrow ? vw : clamp(rightEdge - left, MIN_W, MAX_W);
      if (!narrow && left + width > rightEdge) left = Math.max(0, rightEdge - width);

      setStyleIfDiff(panel, 'left',  `${narrow ? 0 : left}px`);
      setStyleIfDiff(panel, 'top',   `${top}px`);
      setStyleIfDiff(panel, 'width', `${width}px`);
      setStyleIfDiff(panel, 'maxWidth', `${MAX_W}px`);

      // ---- Height (content-first, accurate with padding/border) ----
      const headerH = header ? header.offsetHeight : 0;

      // Natural content height (inside .mot-modal__content)
      const contentH = content ? content.scrollHeight : (bodyEl ? bodyEl.scrollHeight : 0);

      // Body box model
      const cs = bodyEl ? getComputedStyle(bodyEl) : null;
      const isBorderBox = cs ? cs.boxSizing === 'border-box' : false;
      const pTop  = cs ? parseFloat(cs.paddingTop)    || 0 : 0;
      const pBot  = cs ? parseFloat(cs.paddingBottom) || 0 : 0;
      const bTop  = cs ? parseFloat(cs.borderTopWidth)    || 0 : 0;
      const bBot  = cs ? parseFloat(cs.borderBottomWidth) || 0 : 0;
      const bodyPadBorder = pTop + pBot + bTop + bBot;

      // Max panel/body caps allowed by viewport
      const cap       = Math.max(0, vh - top - BOTTOM_MARGIN);
      const maxBodyH  = Math.max(0, cap - headerH);

      // Effective needed height for the scrolling region INCLUDING padding/border
      const neededBodyH = contentH + bodyPadBorder;

      // Does it fit without needing scroll?
      const fitsWithoutScroll = neededBodyH <= maxBodyH;

      // Panel wants to hug content (including body padding/border) up to the cap
      const desiredPanelH = Math.min(headerH + neededBodyH, cap);
      setStyleIfDiff(panel, 'maxHeight', `${cap}px`);
      setStyleIfDiff(panel, 'height',    `${desiredPanelH}px`);

      // Body sizing
      let unlockedBody = false;
      if (fitsWithoutScroll) {
        if (bodyEl.style.height) { bodyEl.style.height = ''; unlockedBody = true; }
        setStyleIfDiff(bodyEl, 'maxHeight', `${maxBodyH}px`);
      } else {
        const fixedContentBoxH = Math.max(0, maxBodyH - (isBorderBox ? 0 : bodyPadBorder));
        setStyleIfDiff(bodyEl, 'maxHeight', `${maxBodyH}px`);
        setStyleIfDiff(bodyEl, 'height',    `${fixedContentBoxH}px`);
      }

      // If we just unlocked body height, remeasure next frame so the panel can shrink immediately
      if (unlockedBody) {
        requestAnimationFrame(() => { if (anchorEl) computeAndPlaceNow(); });
      }
    });
  }

  function scheduleReposition() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(() => { rafScheduled = false; computeAndPlaceNow(); });
  }

  // ---------- Keyboard handling ----------
  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const f = panel.querySelectorAll(focusableSelectors);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  function onKeyDown(e) {
    if (e.key === 'Escape') close('esc');
    else if (e.key === 'Enter' && document.activeElement === header) { e.preventDefault(); close('header_enter'); }
    else trapFocus(e);
  }

  // ---------- Observers ----------
  function observeTriggerVisibility() {
    if (!('IntersectionObserver' in window) || !anchorEl) return;
    if (io) { try { io.disconnect(); } catch(_){} io = null; }
    io = new IntersectionObserver(() => { ensureTriggerStillVisibleOrClose(); }, { root: null, threshold: [0, 0.01] });
    io.observe(anchorEl);
  }
  function unobserveTriggerVisibility() { if (io) { try { io.disconnect(); } catch(_){} io = null; } }

  function observeLiveResize() {
    if ('ResizeObserver' in window) {
      if (roPanel)   { try { roPanel.disconnect(); } catch(_){} }
      if (roBody)    { try { roBody.disconnect(); } catch(_){} }
      if (roContent) { try { roContent.disconnect(); } catch(_){} }

      roPanel   = new ResizeObserver(scheduleReposition);
      roBody    = new ResizeObserver(scheduleReposition);
      roContent = new ResizeObserver(scheduleReposition);
      roPanel.observe(panel);
      if (bodyEl)  roBody.observe(bodyEl);
      if (content) roContent.observe(content);
    }

    if ('MutationObserver' in window && content) {
      if (mo) { try { mo.disconnect(); } catch(_){} }
      mo = new MutationObserver(() => { scheduleReposition(); burstRecalc(3); });
      mo.observe(content, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style','class','open','hidden','aria-expanded']
      });
    }

    if (!imgListenerBound && content) {
      imgListenerBound = true;
      content.addEventListener('load', (e) => {
        const t = e.target;
        if (t && (t.tagName === 'IMG' || t.tagName === 'VIDEO' || t.tagName === 'IFRAME')) {
          scheduleReposition(); burstRecalc(3);
        }
      }, true);
    }
  }
  function unobserveLiveResize() {
    if (roPanel)   { try { roPanel.disconnect(); } catch(_){} roPanel = null; }
    if (roBody)    { try { roBody.disconnect(); } catch(_){} roBody = null; }
    if (roContent) { try { roContent.disconnect(); } catch(_){} roContent = null; }
    if (mo)        { try { mo.disconnect(); } catch(_){} mo = null; }
  }

  function onMQChange(e) { if (e.matches) close('desktop'); }

  // ---------- Open / Close ----------
  async function open(triggerEl) {
    if (isDesktop() || opening || closing) return;
    if (!modal.hasAttribute('hidden')) return;

    anchorEl = triggerEl || document.querySelector('a.refine-Mobile');
    if (!anchorEl) return;
    opening = true;

    await scrollTriggerIntoFixedPosition(anchorEl);
    if (isDesktop() || closing) {
      opening = false;
      return;
    }

    if (bodyEl) bodyEl.style.overflowAnchor = 'none';

    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    lastFocused = document.activeElement;

    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('hidden');

    setBackgroundInert(true);
    setPageScrollLock(true);

    if (bodyEl) bodyEl.scrollTop = 0;
    computeAndPlaceNow();
    if (bodyEl) bodyEl.scrollTop = 0;

    requestAnimationFrame(() => {
      if (closing) {
        opening = false;
        return;
      }
      if (isDesktop()) {
        opening = false;
        close('desktop');
        return;
      }
      modal.classList.add('is-open');
      if (anchorEl) anchorEl.setAttribute('aria-expanded', 'true');
      if (header) safeFocus(header);

      document.addEventListener('keydown', onKeyDown, { passive: false });
      window.addEventListener('resize', scheduleReposition, { passive: true });
      window.addEventListener('scroll', scheduleReposition, { passive: true });

      if (visualViewport) {
        visualViewport.addEventListener('resize', scheduleReposition, { passive: true });
        visualViewport.addEventListener('scroll',  scheduleReposition, { passive: true });
      }
      if (mqlDesktop) {
        if (mqlDesktop.addEventListener) mqlDesktop.addEventListener('change', onMQChange);
        else if (mqlDesktop.addListener) mqlDesktop.addListener(onMQChange);
      }

      observeTriggerVisibility();
      observeLiveResize();
      if (bodyEl) bodyEl.scrollTop = 0;
      burstRecalc(4);
      opening = false;
    });
  }

  function close(reason) {
    reason = reason || 'user';
    if (closing) return;
    if (modal.hasAttribute('hidden') && !modal.classList.contains('is-open')) return;

    closing = true;
    closeFinished = false;
    opening = false;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (anchorEl) anchorEl.setAttribute('aria-expanded', 'false');

    const finish = () => {
      if (closeFinished) return;
      closeFinished = true;
      modal.setAttribute('hidden', '');
      panel.removeEventListener('transitionend', finish);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', scheduleReposition);
      window.removeEventListener('scroll', scheduleReposition);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', scheduleReposition);
        visualViewport.removeEventListener('scroll',  scheduleReposition);
      }
      if (mqlDesktop) {
        if (mqlDesktop.removeEventListener) mqlDesktop.removeEventListener('change', onMQChange);
        else if (mqlDesktop.removeListener) mqlDesktop.removeListener(onMQChange);
      }
      unobserveTriggerVisibility();
      unobserveLiveResize();

      if (bodyEl) bodyEl.style.overflowAnchor = '';

      setPageScrollLock(false);
      setBackgroundInert(false);

      const shouldRestore =
        lastFocused &&
        reason !== 'desktop' &&
        reason !== 'trigger_hidden' &&
        isElementVisible(lastFocused);
      if (shouldRestore) safeFocus(lastFocused);

      lastFocused = null;
      anchorEl = null;
      closing = false;
    };

    if (prefersReducedMotion) {
      finish();
      return;
    }
    panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 300);
  }

  // Header closes modal
  if (header) {
    header.addEventListener('click', () => close('user'));
    header.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); close('header_enter'); }
    });
  }
  if (overlay) overlay.addEventListener('click', () => close('overlay'));

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('a.refine-Mobile');
    if (!trigger) return;
    e.preventDefault();
    open(trigger);
  });

  window.MOTRefineModal = { open, close, reposition: computeAndPlaceNow };
})();
